/**
 * Shelf Sidebar store — Phase 6 (Patent Candidate I).
 *
 * Plan reference:
 *   docs/UX_V3_AI_NATIVE_RECONCEPTUALIZATION_PLAN.md
 *   §"Phase 6 — Shelf sidebar" and §"The creative sidebar".
 *
 * State machine summary:
 *   - ``openShelfId`` is the SINGLE persistent shelf-open id; click-pin
 *     sets it, click again (or click another shelf) replaces it,
 *     ``setOpenShelf(null)`` closes.
 *   - ``hoverIntentShelfId`` is a SEPARATE field set after the
 *     200 ms dwell timer fires. It does NOT pin and is cleared on
 *     mouseleave. Keeping it distinct from ``openShelfId`` is what
 *     prevents hover-of-death — a hover that becomes a click pins
 *     by transferring the value into ``openShelfId``; a hover that
 *     leaves clears only the hover field.
 *   - The Felix-driven open path (``sidebar.open_category`` tool)
 *     calls ``setOpenShelf(id)`` directly via the window-event bridge.
 *
 * Persistence is per-user via ``localStorage`` keyed by ftl_user_id
 * so a logged-in user retains their preferred open shelf across
 * page reloads but no leak occurs across user sessions on the same
 * browser.
 */

import { create } from "zustand";

export type ShelfId =
  | "plan"
  | "design"
  | "execute"
  | "verify"
  | "govern"
  | "broadcast";

export const SHELF_IDS: readonly ShelfId[] = [
  "plan",
  "design",
  "execute",
  "verify",
  "govern",
  "broadcast",
] as const;

const PERSIST_KEY_PREFIX = "ftl_shelf_open_v1::";

function persistKey(): string {
  let userId = "anon";
  try {
    userId = localStorage.getItem("ftl_user_id") || "anon";
  } catch {
    /* ignore */
  }
  return `${PERSIST_KEY_PREFIX}${userId}`;
}

function loadPersisted(): ShelfId | null {
  try {
    const raw = localStorage.getItem(persistKey());
    if (raw && (SHELF_IDS as readonly string[]).includes(raw)) {
      return raw as ShelfId;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function savePersisted(id: ShelfId | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(persistKey());
    } else {
      localStorage.setItem(persistKey(), id);
    }
  } catch {
    /* ignore */
  }
}

interface SidebarShelfState {
  /** Click-pinned shelf. Null = nothing pinned. Single-shelf invariant. */
  openShelfId: ShelfId | null;
  /** Shelf that hover-with-intent has dwell-promoted. Cleared on
   *  mouseleave or shelf-pin. */
  hoverIntentShelfId: ShelfId | null;
  /** Per-user persisted open shelf (mirrors openShelfId; kept as a
   *  separate field so tests can inspect what landed in localStorage
   *  without hitting the DOM). */
  persistedOpenShelfId: ShelfId | null;

  /** Click-pin a shelf. ``id = null`` closes everything. */
  setOpenShelf: (id: ShelfId | null) => void;
  /** Hover-with-intent dwell handler. Does NOT pin — separate field
   *  prevents hover-of-death from sticking a wandering pointer open. */
  setHoverIntentShelf: (id: ShelfId | null) => void;
  /** Clear only the hover-intent state (the click-pinned shelf
   *  survives). Called from mouseleave. */
  clearHover: () => void;
}

export const useSidebarStore = create<SidebarShelfState>((set) => {
  const initial = loadPersisted();
  return {
    openShelfId: initial,
    hoverIntentShelfId: null,
    persistedOpenShelfId: initial,
    setOpenShelf: (id) => {
      savePersisted(id);
      set({
        openShelfId: id,
        // Pinning replaces hover-intent; preserves the
        // single-shelf-open invariant: at most one of
        // (openShelfId, hoverIntentShelfId) is the source of truth
        // at any time, and the pin wins.
        hoverIntentShelfId: null,
        persistedOpenShelfId: id,
      });
    },
    setHoverIntentShelf: (id) => set({ hoverIntentShelfId: id }),
    clearHover: () => set({ hoverIntentShelfId: null }),
  };
});

// ---------------------------------------------------------------------
// Felix-driven open bridge
// ---------------------------------------------------------------------
//
// The Felix ``sidebar.open_category`` tool serializes into a custom DOM
// event so the ShelfSidebar can react without coupling Zustand to the
// chat-stream layer. The Iterator-page chat handler dispatches:
//
//   window.dispatchEvent(
//     new CustomEvent("ftl.felix.sidebar.open_category", {
//       detail: { category_id: "plan", source: "felix_chat" },
//     }),
//   );
//
// This subscriber turns that into a store mutation. Returns an
// ``unsubscribe`` closure for React StrictMode + tests.

export interface FelixCategoryOpenDetail {
  category_id: ShelfId;
  source?: "felix_chat" | "hover_intent" | "click";
}

export const FELIX_SIDEBAR_OPEN_EVENT = "ftl.felix.sidebar.open_category";

export function subscribeToFelixCategoryOpen(): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  const handler = (evt: Event) => {
    const ce = evt as CustomEvent<FelixCategoryOpenDetail>;
    const id = ce?.detail?.category_id;
    if (
      typeof id === "string" &&
      (SHELF_IDS as readonly string[]).includes(id)
    ) {
      useSidebarStore.getState().setOpenShelf(id as ShelfId);
    }
  };
  window.addEventListener(FELIX_SIDEBAR_OPEN_EVENT, handler);
  return () => window.removeEventListener(FELIX_SIDEBAR_OPEN_EVENT, handler);
}
